// components/partial-pages/GeneralSettingsPage/WebhookSettings.tsx
/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tooltip,
} from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as CopyIcon,
  VpnKey as RotateIcon,
} from '@material-ui/icons'
import { BORDER1_COLOR, PRIMARY_COLOR, TEXTFIELD_LABEL_COLOR, GRAY3_COLOR } from 'lib/constants'
import type { Webhook, WebhookDirection, WebhookWithSecret } from 'lib/types/webhooks'
import {
  getWebhooks,
  createWebhook,
  updateWebhook,
  rotateWebhookSecret,
  deleteWebhook,
  webhookErrorMessage,
} from 'models/api/webhooks'

const StyledContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '15px',
})
const StyledTitleText = styled.div({
  color: PRIMARY_COLOR,
  fontFamily: 'Montserrat',
  fontSize: '1.8rem',
  fontWeight: 'bold',
  lineHeight: '2.9rem',
  textAlign: 'left',
})
const StyledDescriptionText = styled.div({
  fontFamily: 'Montserrat',
  fontSize: '1.4rem',
  fontWeight: 'bold',
  lineHeight: '1.9rem',
  textAlign: 'left',
})
const StyledSeparator = styled.div({
  border: `1px solid ${BORDER1_COLOR}`,
  marginTop: '12px',
  marginBottom: '20px',
})
const Row = styled.div({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 0,
  paddingBottom: 4,
})
const ColTitle = styled.span({
  fontFamily: 'Montserrat',
  fontWeight: 'bold',
  fontSize: '1.4rem',
  letterSpacing: '0.1rem',
  textTransform: 'uppercase',
  color: PRIMARY_COLOR,
  flex: 1,
  lineHeight: '1.4rem',
})
const ColSub = styled.span({
  fontFamily: 'Montserrat',
  fontWeight: 'bold',
  fontSize: '1.2rem',
  color: '#242424',
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
})
const UrlCol = styled(ColSub)({
  flex: 2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
})
const UrlTitle = styled(ColTitle)({ flex: 2 })
const ActionsCol = styled(ColSub)({
  justifyContent: 'flex-end',
  gap: 4,
  flex: 0.8,
})
const HeaderActionsSlot = styled.div({
  flex: 0.8,
  display: 'flex',
  justifyContent: 'flex-end',
})
const StyledButton = styled(Button)({
  color: PRIMARY_COLOR,
  padding: '8px 20px',
  border: `2px solid ${PRIMARY_COLOR}`,
  fontFamily: 'Montserrat',
  boxShadow: '0px 4px 8px rgba(20, 48, 72, 0.1)',
  fontSize: '1.0rem',
  fontWeight: 'bold',
  letterSpacing: '0.1rem',
  marginRight: '5px',
  cursor: 'pointer',
  borderRadius: '2px',
})
const FieldBlock = styled.div({ width: '100%' })
const FieldLabel = styled(InputLabel)({
  color: TEXTFIELD_LABEL_COLOR,
  fontFamily: 'Montserrat',
  fontSize: '1.4rem',
  fontWeight: 'bold',
  lineHeight: '1.5rem',
  letterSpacing: '0.1rem',
  textAlign: 'left',
  marginBottom: '20px',
})
const FieldInput = styled(Input)({
  background: GRAY3_COLOR,
  borderRadius: '10px',
  padding: '5px 10px',
  fontFamily: 'Montserrat',
})
const FieldSelect = styled(Select)({
  background: GRAY3_COLOR,
  borderRadius: '10px',
  padding: '5px 10px',
  fontFamily: 'Montserrat',
  width: '100%',
})
const HelpText = styled.div({
  fontFamily: 'Montserrat',
  fontSize: '1.1rem',
  color: '#555',
  marginTop: 6,
})
const ErrorText = styled.div({
  fontFamily: 'Montserrat',
  fontSize: '1.2rem',
  fontWeight: 'bold',
  color: '#c62828',
})

// Brand teal toggle, matching GeofenceSettings and VoiceLicenseToggleSection
const ToggleSwitch = withStyles({
  switchBase: {
    color: 'white',
    '&$checked': { color: 'rgba(48, 188, 176, 1)' },
    '&$checked + $track': { backgroundColor: 'rgba(48, 188, 176, 0.33)' },
  },
  checked: {},
  track: {},
})(Switch)

const DIRECTION_LABEL: Record<WebhookDirection, string> = {
  unidirectional: 'Receive only',
  bidirectional: 'Receive and close alerts',
}

const DIRECTION_HELP: Record<WebhookDirection, string> = {
  unidirectional: 'Alert updates are sent to this URL.',
  bidirectional: 'Alert updates are sent to this URL, and the receiver may reply to close the alert.',
}

type Props = { orgId?: string }

export default function WebhookSettings({ orgId }: Props) {
  const [rows, setRows] = useState<Webhook[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  // create/edit form state
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [direction, setDirection] = useState<WebhookDirection>('unidirectional')
  const [enabled, setEnabled] = useState(true)

  // the one-time secret reveal after create or rotate
  const [revealed, setRevealed] = useState<WebhookWithSecret | null>(null)
  const [copied, setCopied] = useState(false)

  const load = async () => {
    if (!orgId) return
    setLoading(true)
    setLoadError(null)
    try {
      setRows(await getWebhooks(orgId))
    } catch (e) {
      setLoadError(webhookErrorMessage(e, 'Unable to load webhooks.'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [orgId]) // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditingId(null)
    setName('')
    setUrl('')
    setDirection('unidirectional')
    setEnabled(true)
    setFormError(null)
    setModalOpen(true)
  }

  const openEdit = (row: Webhook) => {
    setEditingId(row.id)
    setName(row.name)
    setUrl(row.url)
    setDirection(row.direction)
    setEnabled(row.enabled)
    setFormError(null)
    setModalOpen(true)
  }

  const showSecret = (webhook: WebhookWithSecret) => {
    setCopied(false)
    setRevealed(webhook)
  }

  const trimmedName = name.trim()
  const trimmedUrl = url.trim()
  const isValid = !!trimmedName && /^https:\/\/\S+$/i.test(trimmedUrl)

  const onSave = async () => {
    if (!orgId || !isValid) return
    const fields = { name: trimmedName, url: trimmedUrl, direction, enabled }
    setSaving(true)
    setFormError(null)
    try {
      if (editingId) {
        await updateWebhook(editingId, orgId, fields)
      } else {
        showSecret(await createWebhook(orgId, fields))
      }
      setModalOpen(false)
      await load()
    } catch (e) {
      setFormError(webhookErrorMessage(e, 'Unable to save webhook.'))
    } finally {
      setSaving(false)
    }
  }

  const onToggleEnabled = async (row: Webhook) => {
    if (!orgId) return
    setBusyId(row.id)
    try {
      const updated = await updateWebhook(row.id, orgId, { enabled: !row.enabled })
      setRows(prev => prev.map(r => (r.id === row.id ? { ...r, enabled: updated.enabled } : r)))
    } catch (e) {
      window.alert(webhookErrorMessage(e, 'Unable to update webhook.'))
    } finally {
      setBusyId(null)
    }
  }

  const onRotate = async (row: Webhook) => {
    if (!orgId) return
    const ok = window.confirm(
      `Generate a new secret for "${row.name}"? The current secret stops working immediately, so the receiver must be updated.`,
    )
    if (!ok) return
    setBusyId(row.id)
    try {
      showSecret(await rotateWebhookSecret(row.id, orgId))
    } catch (e) {
      window.alert(webhookErrorMessage(e, 'Unable to rotate secret.'))
    } finally {
      setBusyId(null)
    }
  }

  const onDelete = async (row: Webhook) => {
    if (!orgId) return
    const ok = window.confirm(`Delete the webhook "${row.name}"?`)
    if (!ok) return
    setBusyId(row.id)
    try {
      await deleteWebhook(row.id, orgId)
      await load()
    } catch (e) {
      window.alert(webhookErrorMessage(e, 'Unable to delete webhook.'))
    } finally {
      setBusyId(null)
    }
  }

  const onCopySecret = async () => {
    if (!revealed) return
    try {
      await navigator.clipboard.writeText(revealed.secret)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  const onEnterKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (!saving && isValid) onSave()
    }
  }

  return (
    <StyledContainer>
      <StyledTitleText>Webhooks</StyledTitleText>
      <StyledDescriptionText>
        Send alert updates for this organization to an HTTPS endpoint. Each webhook has a secret the receiver uses to
        verify deliveries.
      </StyledDescriptionText>

      {/* Header */}
      <Row>
        <ColTitle>Name</ColTitle>
        <UrlTitle>URL</UrlTitle>
        <ColTitle>Direction</ColTitle>
        <ColTitle>Enabled</ColTitle>
        <HeaderActionsSlot>
          <StyledButton onClick={openCreate} disabled={!orgId} startIcon={<AddIcon />}>
            Add Webhook
          </StyledButton>
        </HeaderActionsSlot>
      </Row>

      <StyledSeparator />

      {/* Body */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress size={28} />
        </Box>
      ) : loadError ? (
        <ErrorText>{loadError}</ErrorText>
      ) : rows.length === 0 ? (
        <span style={{ fontFamily: 'Montserrat', fontWeight: 'bold', fontSize: '1.2rem', color: '#29a096' }}>
          No webhooks yet.
        </span>
      ) : (
        rows.map(row => (
          <Row key={row.id} style={{ marginBottom: 24 }}>
            <ColSub>{row.name}</ColSub>
            <UrlCol title={row.url}>{row.url}</UrlCol>
            <ColSub>{DIRECTION_LABEL[row.direction] ?? row.direction}</ColSub>
            <ColSub>
              <ToggleSwitch
                size="small"
                checked={row.enabled}
                disabled={busyId === row.id}
                onChange={() => onToggleEnabled(row)}
                inputProps={{ 'aria-label': `Enable ${row.name}` }}
              />
            </ColSub>
            <ActionsCol>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => openEdit(row)} disabled={busyId === row.id}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rotate secret">
                <IconButton size="small" onClick={() => onRotate(row)} disabled={busyId === row.id}>
                  <RotateIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => onDelete(row)} disabled={busyId === row.id}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </ActionsCol>
          </Row>
        ))
      )}

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? 'Edit Webhook' : 'Add Webhook'}</DialogTitle>
        <DialogContent>
          <Box display="grid" gridGap={16} mt={1}>
            <FieldBlock>
              <FieldLabel htmlFor="webhook-name">Name</FieldLabel>
              <FieldInput
                id="webhook-name"
                disableUnderline
                fullWidth
                placeholder="e.g. Dispatch console"
                autoFocus
                inputProps={{ maxLength: 255 }}
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={onEnterKey}
              />
            </FieldBlock>

            <FieldBlock>
              <FieldLabel htmlFor="webhook-url">URL</FieldLabel>
              <FieldInput
                id="webhook-url"
                disableUnderline
                fullWidth
                placeholder="https://hooks.example.com/reactmobile"
                value={url}
                onChange={e => setUrl(e.target.value)}
                onKeyDown={onEnterKey}
              />
              <HelpText>Must be a public https:// address.</HelpText>
            </FieldBlock>

            <FieldBlock>
              <FieldLabel htmlFor="webhook-direction">Direction</FieldLabel>
              <FieldSelect
                id="webhook-direction"
                disableUnderline
                value={direction}
                onChange={e => setDirection(e.target.value as WebhookDirection)}
              >
                <MenuItem value="unidirectional">{DIRECTION_LABEL.unidirectional}</MenuItem>
                <MenuItem value="bidirectional">{DIRECTION_LABEL.bidirectional}</MenuItem>
              </FieldSelect>
              <HelpText>{DIRECTION_HELP[direction]}</HelpText>
            </FieldBlock>

            <FieldBlock>
              <Box display="flex" alignItems="center">
                <ToggleSwitch
                  checked={enabled}
                  onChange={e => setEnabled(e.target.checked)}
                  inputProps={{ 'aria-label': 'Enabled' }}
                />
                <span style={{ fontFamily: 'Montserrat', fontWeight: 'bold', fontSize: '1.2rem' }}>Enabled</span>
              </Box>
            </FieldBlock>

            {formError && <ErrorText>{formError}</ErrorText>}
          </Box>
        </DialogContent>
        <DialogActions>
          <StyledButton onClick={() => setModalOpen(false)}>Cancel</StyledButton>
          <StyledButton onClick={onSave} disabled={saving || !isValid}>
            {saving ? 'Saving…' : 'Save'}
          </StyledButton>
        </DialogActions>
      </Dialog>

      {/* One-time secret reveal */}
      <Dialog open={!!revealed} onClose={() => setRevealed(null)} fullWidth maxWidth="sm">
        <DialogTitle>Webhook secret</DialogTitle>
        <DialogContent>
          <Box display="grid" gridGap={16} mt={1}>
            <StyledDescriptionText>
              Copy this secret for “{revealed?.name}” now. It will not be shown again; rotate the secret if it is lost.
            </StyledDescriptionText>
            <FieldInput
              disableUnderline
              fullWidth
              readOnly
              value={revealed?.secret ?? ''}
              onFocus={e => e.target.select()}
              endAdornment={
                <InputAdornment position="end">
                  <Tooltip title={copied ? 'Copied' : 'Copy'}>
                    <IconButton size="small" onClick={onCopySecret} aria-label="Copy secret">
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <StyledButton onClick={() => setRevealed(null)}>Done</StyledButton>
        </DialogActions>
      </Dialog>
    </StyledContainer>
  )
}
